import React, { useMemo } from "react";
import DataTable from "react-data-table-component";
import moment from "moment";
import { PropagateLoader } from "react-spinners";
import NoData from "../../molecules/NoData";
import { formatCurrency } from "../../../utils/formatCurrency";
import { getInmobiliaria } from "../../../utils/getInmobiliaria";

const StatsTable = ({ loading, dataInmuebles = [], dataGastos = [], dataRentas = [], dataMantenimiento = [] }) => {
  const hoy = useMemo(() => moment().startOf("day"), []);

  const getStartDate = (rent) => {
    if (!rent?.month) return null;
    const first = String(rent.month).split(" - ")[0]?.trim();
    return first ? moment(first, "YYYY-MM-DD", true) : null;
  };

  const rows = useMemo(() => {
    const map = new Map();

    for (const inm of dataInmuebles) {
      map.set(inm.id, {
        id: inm.id,
        name: inm.name || inm.title || `Inmueble #${inm.id}`,
        type: inm.type || "-",
        real_estate: inm.real_estate ?? "-",
        gastosCount: 0,
        gastosTotal: 0,
        mantoCount: 0,
        mantoTotal: 0,
        rentasCobradasCount: 0,
        rentasCobradasTotal: 0,
        rentasAtrasadasCount: 0,
        rentasAtrasadasTotal: 0,
        rentasProgramadasCount: 0,
        rentasProgramadasTotal: 0,
      });
    }

    for (const g of dataGastos) {
      const row = map.get(g.propertieId);
      if (!row) continue;
      const monto = Number(g.amount ?? g.monto ?? 0);
      row.gastosCount += 1;
      row.gastosTotal += isNaN(monto) ? 0 : monto;
    }

    for (const m of dataMantenimiento) {
      const row = map.get(m.propertieId);
      if (!row) continue;
      const monto = Number(m.cost ?? m.total ?? 0);
      row.mantoCount += 1;
      row.mantoTotal += isNaN(monto) ? 0 : monto;
    }

    for (const r of dataRentas) {
      const row = map.get(r.propertieId);
      if (!row) continue;
      const total = Number(r.total ?? r.subtotal ?? 0) || 0;

      if (r.status === true) {
        row.rentasCobradasCount += 1;
        row.rentasCobradasTotal += total;
        continue;
      }

      const start = getStartDate(r);
      if (start?.isValid()) {
        if (start.isSameOrBefore(hoy, "day")) {
          row.rentasAtrasadasCount += 1;
          row.rentasAtrasadasTotal += total;
        } else if (start.isAfter(hoy, "day")) {
          row.rentasProgramadasCount += 1;
          row.rentasProgramadasTotal += total;
        }
      }
    }

    return Array.from(map.values());
  }, [dataInmuebles, dataGastos, dataMantenimiento, dataRentas, hoy]);

  const columns = useMemo(
    () => [
      { name: "Inmueble", selector: (row) => row.name, sortable: true, wrap: true },
      { name: "Tipo", selector: (row) => row.type, sortable: true, width: "140px" },
      { name: "Inmobiliaria", selector: (row) => getInmobiliaria(row.real_estate), sortable: true, width: "140px" },

      {
        name: "Gastos",
        cell: (row) => (
          <div className="text-xs">
            <div>{row.gastosCount} mov.</div>
            <div className="font-medium">{formatCurrency(row.gastosTotal)}</div>
          </div>
        ),
        sortable: true,
        sortFunction: (a, b) => a.gastosTotal - b.gastosTotal,
        width: "140px",
      },

      {
        name: "Mantenimiento",
        cell: (row) => (
          <div className="text-xs">
            <div>{row.mantoCount} mov.</div>
            <div className="font-medium">{formatCurrency(row.mantoTotal)}</div>
          </div>
        ),
        sortable: true,
        sortFunction: (a, b) => a.mantoTotal - b.mantoTotal,
        width: "160px",
      },

      {
        name: "Rentas (C / A / P)",
        cell: (row) => (
          <div className="text-xs leading-tight">
            <div>
              <span className="font-medium">C:</span>{" "}
              {row.rentasCobradasCount} | {formatCurrency(row.rentasCobradasTotal)}
            </div>
            <div>
              <span className="font-medium">A:</span>{" "}
              {row.rentasAtrasadasCount} | {formatCurrency(row.rentasAtrasadasTotal)}
            </div>
            <div>
              <span className="font-medium">P:</span>{" "}
              {row.rentasProgramadasCount} | {formatCurrency(row.rentasProgramadasTotal)}
            </div>
          </div>
        ),
        sortable: true,
        sortFunction: (a, b) => {
          const ta =
            a.rentasCobradasTotal + a.rentasAtrasadasTotal + a.rentasProgramadasTotal;
          const tb =
            b.rentasCobradasTotal + b.rentasAtrasadasTotal + b.rentasProgramadasTotal;
          return ta - tb;
        },
        grow: 2,
      },
    ],
    []
  );

  return (
    <div className="mt-4">
      <DataTable
        data={rows}
        progressPending={loading}
        columns={columns}
        progressComponent={<div className="w-full h-20 flex justify-center items-center"> <PropagateLoader color="red" /> </div>}
        noDataComponent={<NoData />}
        pagination
        highlightOnHover
        dense
      />
    </div>
  );
};

export default StatsTable;
